/* Peak Moto fit engine.
 *
 * Shared by the collection filters and the Product Recommender so that a
 * rider's measurements give the same size everywhere. The size itself comes
 * from PmoSizeCalc (assets/pmo-size-calc.js), the same maths the product
 * page size guide uses; this file works out which of a product's charts and
 * fields apply to the rider, turns the result into a fit level, and maps the
 * chart's size label onto the product's own size and length options.
 *
 * The rider profile is kept in this browser (localStorage) so it follows the
 * shopper from collection to collection.
 */
(function (root) {
  "use strict";

  var PROFILE_KEY = "pmo.rider.v1";

  // Body areas, matched against a size chart's calculator field labels.
  // Same list and order as the size guide (hand length before palm).
  var AREAS = [
    ["hand length", /^(hand length|hand size)/],
    ["palm", /^(palm|hand circumference|hand girth|hand)/],
    ["chest", /^(chest|bust)/],
    ["waist", /^waist/],
    ["hips", /^(hips?|seat)/],
    ["thigh", /^(mid[- ]?thigh|thigh)/],
    ["inseam", /^(inseam|inside leg|leg length)/],
    ["head", /^head/],
    ["foot", /^(foot|feet)/],
    ["height", /^height/]
  ];
  var LEG = { inseam: 1, height: 1 };

  function areaName(label) {
    var a = String(label || "").toLowerCase().replace(/\(.*?\)/g, "").replace(/\b(circumference|girth|measurement)\b/g, "").replace(/\s+/g, " ").trim();
    return a || String(label || "").toLowerCase();
  }
  // "Full Bust / Chest", "Natural Waist", "Lower Hip": try each name, without
  // the qualifier in front.
  function areaKey(label) {
    var parts = areaName(label).split(/\s*\/\s*/);
    for (var p = 0; p < parts.length; p++) {
      var t = parts[p].replace(/^(full|natural|lower|upper|true|body)\s+/, "");
      for (var i = 0; i < AREAS.length; i++) if (AREAS[i][1].test(t)) return AREAS[i][0];
    }
    return null;
  }
  // Charts of the garment's own measurements can't be compared with a body.
  function isGarment(label) {
    if (/palm|hand|head|foot/i.test(label || "")) return false;
    return /\bflat\b|width|armpit|shoulder|\bof (a )?(jacket|garment|hoodie|tee|shirt|pants|jeans)\b/i.test(label || "");
  }

  function norm(s) {
    return String(s == null ? "" : s).trim().toLowerCase();
  }

  // ---------- sizes and lengths ----------

  var LETTER = { XXXXS: "4XS", XXXS: "3XS", XXS: "2XS", XS: "XS", S: "S", SM: "S", M: "M", MD: "M", L: "L", LG: "L", XL: "XL", XXL: "2XL", XXXL: "3XL", XXXXL: "4XL", XXXXXL: "5XL" };
  var LETTER_ORDER = ["4XS", "3XS", "2XS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL", "7XL", "8XL"];

  // One size token ("XXS", "AU 6", "EU 42", "W32", "42") as a filter chip.
  function chipOf(token, ctx) {
    var t = String(token || "").replace(/\(.*?\)/g, "").trim();
    if (!t) return null;
    // "4½" and "4.5" are the same size; half sizes show as ½.
    var u = t.toUpperCase().replace(/\s+/g, " ").replace(/\s*½/g, ".5");
    var half = function (n) { return n.replace(",", ".").replace(/\.5$/, "½"); };
    var m;
    if (LETTER[u]) return { sys: "L", v: LETTER[u] };
    if ((m = u.match(/^([2-8])\s?X\s?([SL])$/))) return { sys: "L", v: m[1] + "X" + m[2] };
    // Charts sometimes put the number first: "14 AU", "42 EU", "12 US".
    if ((m = u.match(/^(\d{1,2}(?:[.,]5)?)\s?(AU|EU|US)$/))) u = m[2] + " " + m[1];
    if ((m = u.match(/^AU\s?(\d{1,2})$/))) return { sys: "AU", v: "AU " + +m[1] };
    if ((m = u.match(/^EU\s?(\d{2}(?:[.,]5)?)$/))) return { sys: "EU", v: "EU " + half(m[1]) };
    if ((m = u.match(/^US\s?(\d{1,2}(?:[.,]5)?)$/))) return { sys: "US", v: "US " + half(m[1]) };
    if ((m = u.match(/^W\s?(\d{2})$/))) return { sys: "W", v: "W" + m[1] };
    if ((m = u.match(/^(\d{2}(?:[.,]5)?)$/))) {
      var n = m[1].replace(",", ".");
      if (ctx === "boots") return { sys: "EU", v: "EU " + half(n) };
      if (ctx === "pants" && +n >= 24 && +n <= 50) return { sys: "W", v: "W" + n };
      if (ctx === "helmets" && +n >= 50 && +n <= 66) return { sys: "CM", v: n + " cm" };
      return { sys: "O", v: n };
    }
    if (/^L\s?\d{2}/.test(u)) return null; // leg length, not a size
    return { sys: "O", v: t };
  }

  // A letter range ("XS-S", "S-XL") is every letter size it spans.
  function letterRange(tok) {
    var m = String(tok || "").trim().match(/^(\S+)\s*[-–]\s*(\S+)$/);
    if (!m) return null;
    var a = chipOf(m[1]), b = chipOf(m[2]);
    if (!a || !b || a.sys !== "L" || b.sys !== "L") return null;
    var i = LETTER_ORDER.indexOf(a.v), j = LETTER_ORDER.indexOf(b.v);
    if (i < 0 || j <= i) return null;
    return LETTER_ORDER.slice(i, j + 1).map(function (v) { return { sys: "L", v: v }; });
  }

  // All the chips a size option value stands for: "XS / AU 8" is both XS and AU 8.
  function chipsOf(value, ctx) {
    var out = [];
    String(value || "").split(/\s*[\/|]\s*/).forEach(function (tok) {
      (letterRange(tok) || [chipOf(tok, ctx)]).forEach(function (c) {
        if (c && !out.some(function (x) { return x.v === c.v; })) out.push(c);
      });
    });
    return out;
  }

  function chipRank(c) {
    var i = LETTER_ORDER.indexOf(c.v);
    if (c.sys === "L") return i < 0 ? 99 : i;
    var n = parseFloat(String(c.v).replace(/½/g, ".5").replace(/[^\d.]/g, "")) || 0;
    return ({ AU: 1000, EU: 2000, W: 3000, US: 4000, CM: 5000, O: 6000 })[c.sys] + n;
  }

  // Short includes Petite and Extra Short; Long includes Tall.
  function lengthBucket(text) {
    var t = norm(text);
    if (!t) return null;
    if (/extra short|\bshort\b|petite/.test(t)) return "Short";
    if (/extra long|\blong\b|tall/.test(t)) return "Long";
    if (/standard|regular/.test(t)) return "Regular";
    return null;
  }

  // The space before a length word is optional ("20 USTall" in some charts).
  var LENGTH_RE = /^(.*?)(?:[\s\/]+|(?<=\b(?:US|AU|EU)))(extra short|short|petite|standard|regular|long|tall|extra long|L\d{2}"?)$/i;
  function splitLength(label) {
    var m = String(label == null ? "" : label).trim().match(LENGTH_RE);
    return m && m[1] ? { size: m[1].trim(), length: m[2].trim() } : null;
  }

  // ---------- profile ----------

  function loadProfile() {
    try {
      var p = JSON.parse(localStorage.getItem(PROFILE_KEY) || "null");
      if (p && typeof p === "object") return { meas: p.meas || {}, temp: p.temp || null, gen: p.gen || null };
    } catch (e) {}
    return { meas: {}, temp: null, gen: null };
  }
  function saveProfile(p) {
    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify({ meas: p.meas || {}, temp: p.temp || null, gen: p.gen || null, at: Date.now() }));
    } catch (e) {}
  }
  function measValue(v) {
    var n = parseFloat(v);
    return isFinite(n) && n >= 5 && n <= 250 ? n : null;
  }

  // ---------- fit ----------

  // charts: [{calc, table}] for one product. meas: { chest: 98, ... } in cm.
  // Returns null when no chart can be matched with what the rider gave us,
  // otherwise { level, label, positions, areas, chart }.
  function fitChart(chart, meas) {
    var calc = chart.calc;
    var table = chart.table;
    if (!calc || !table || !table.rows) return null;
    var fields = [];
    var inputs = [];
    var areas = [];
    (calc.fields || []).forEach(function (f) {
      if (isGarment(f.label)) return;
      var k = areaKey(f.label);
      var v = k ? measValue(meas[k]) : null;
      if (v == null) return;
      fields.push(f);
      inputs.push(v);
      areas.push(k);
    });
    if (!fields.some(function (f, i) { return !LEG[areas[i]]; })) return null;
    // "cover" charts list the garment's own measurements and an allowance:
    // the smallest size at least that much bigger than the rider.
    if (calc.mode === "cover") {
      var cv = root.PmoSizeCalc.coverSize(table, { size: calc.size || 0, fields: fields, add: calc.add }, inputs);
      var at = function (n) { return fields.map(function () { return n; }); };
      if (cv.size) return { level: "great", label: cv.size, positions: at(50), areas: areas, score: 100, used: fields.length, span: null };
      var rows = rowsOf(table);
      var last = rows.length > 1 ? cellText(rows[rows.length - 1][calc.size || 0]).trim() : "";
      return last ? { level: "out", label: last, positions: at(0), areas: areas, score: 0, used: fields.length, span: null } : null;
    }
    var sub = { size: calc.size || 0, fields: fields };
    var r = root.PmoSizeCalc.recommend(table, sub, inputs, "regular");
    var pick;
    var level;
    if (r.size && r.ranked.length) {
      pick = r.ranked[0];
      level = pick.positions.every(function (p) { return p >= 17 && p <= 83; }) ? "great" : "good";
    } else {
      pick = r.all.slice().sort(function (a, b) { return b.rawFitScore - a.rawFitScore; })[0];
      if (!pick) return null;
      level = pick.positions.some(function (p) { return p <= 0 || p >= 100; }) ? "out" : "close";
    }
    // Without an inseam every leg length of a size scores the same: say
    // Regular rather than whichever length the chart lists first.
    var xs = splitLength(pick.size);
    if (xs && areas.indexOf("inseam") < 0) {
      var reg = r.all.filter(function (a) {
        var y = splitLength(a.size);
        var same = norm(y ? y.size : a.size) === norm(xs.size);
        return same && (!y || lengthBucket(y.length) === "Regular") && Math.abs(a.rawFitScore - pick.rawFitScore) < 0.01;
      })[0];
      if (reg) pick = reg;
    }
    return { level: level, label: pick.size, positions: pick.positions, areas: areas, score: pick.rawFitScore, used: fields.length, span: spanOf(table, calc, fields, inputs, areas, pick.size) };
  }

  // ---------- between sizes ----------

  function rowsOf(t) {
    if (t.direction !== "row") return t.rows;
    var out = [];
    t.rows.forEach(function (row, r) {
      row.forEach(function (c, i) { (out[i] = out[i] || [])[r] = c; });
    });
    return out;
  }
  function cellText(c) {
    return c ? String(c.v != null ? c.v : c.value != null ? c.value : "") : "";
  }
  function baseOf(label) {
    var x = splitLength(label);
    return norm(x ? x.size : label);
  }
  // Same check as the product page size finder: the size each measurement
  // would pick on its own. When they differ, which sizes they span, and
  // where the suggested size will feel roomy or long. null when they agree.
  function spanOf(table, calc, fields, inputs, areas, size) {
    if (fields.length < 2) return null;
    var col = calc.size || 0;
    var rows = rowsOf(table).slice(1);
    var order = [];
    var labels = {};
    rows.forEach(function (row) {
      var v = cellText(row[col]);
      var b = baseOf(v);
      if (order.indexOf(b) > -1) return;
      order.push(b);
      var x = splitLength(v);
      labels[b] = x ? x.size : v;
    });
    var byLength = order.length < rows.length;
    var each = [];
    fields.forEach(function (f, i) {
      if (byLength && LEG[areas[i]]) return;
      var one = root.PmoSizeCalc.recommend(table, { size: col, fields: [f] }, [inputs[i]], "regular");
      if (one.size) each.push({ area: areas[i], size: labels[baseOf(one.size)] || one.size, rank: order.indexOf(baseOf(one.size)) });
    });
    var ranks = each.map(function (x) { return x.rank; }).filter(function (x) { return x > -1; });
    if (!ranks.length) return null;
    var mine = order.indexOf(baseOf(size));
    var lo = Math.min.apply(null, ranks.concat(mine > -1 ? [mine] : []));
    var hi = Math.max.apply(null, ranks.concat(mine > -1 ? [mine] : []));
    if (hi <= lo) return null;
    var pick = function (test) { return each.filter(function (x) { return x.rank > -1 && test(x); }); };
    var areasOf = function (list) { return list.map(function (x) { return x.area; }); };
    var smaller = pick(function (x) { return x.rank < mine; });
    var bigger = pick(function (x) { return x.rank > mine; });
    var same = pick(function (x) { return x.rank === mine; });
    return {
      from: labels[order[lo]], to: labels[order[hi]], size: mine > -1 ? labels[order[mine]] : size, wide: hi - lo >= 2,
      each: each.map(function (x) { return { area: x.area, size: x.size }; }),
      // What decided the size, and where it won't sit just right.
      fits: areasOf(same.filter(function (x) { return !LEG[x.area]; })),
      legFits: same.some(function (x) { return LEG[x.area]; }),
      roomy: areasOf(smaller.filter(function (x) { return !LEG[x.area]; })),
      long: smaller.some(function (x) { return LEG[x.area]; }),
      snug: areasOf(bigger.filter(function (x) { return !LEG[x.area]; })),
      short: bigger.some(function (x) { return LEG[x.area]; })
    };
  }

  var LEVEL_RANK = { great: 0, good: 1, close: 2, out: 3 };

  // Best fit across a product's charts: the most measurements used, then the
  // best level, then the higher score.
  function fitProduct(charts, meas) {
    var best = null;
    (charts || []).forEach(function (c) {
      var f = fitChart(c, meas);
      if (!f) return;
      if (!best || f.used > best.used || (f.used === best.used && (LEVEL_RANK[f.level] < LEVEL_RANK[best.level] || (f.level === best.level && f.score > best.score)))) best = f;
    });
    return best;
  }

  // A chart size label onto the product's variants. product: { sizeIdx,
  // lenIdx, ctx, vars: [{o:[..], a:bool}] }. Returns the matching variants
  // (preferring a Regular length when the chart doesn't say).
  function variantsFor(product, label) {
    if (product.sizeIdx < 0) return [];
    var x = splitLength(label);
    var base = x ? x.size : label;
    var want = chipsOf(base, product.ctx).map(function (c) { return c.v; });
    // Some charts number their sizes plainly ("38") while the product is sold
    // as "AU 8 / EU 38": try the number as an EU size and as an AU size.
    var bare = String(base == null ? "" : base).trim().match(/^(\d{1,2})(?:[.,](5))?$/);
    if (bare && want.length === 1 && /^\d/.test(want[0])) want.push("EU " + bare[1] + (bare[2] ? "½" : ""), "AU " + +bare[1]);
    var nb = norm(base);
    var hits = product.vars.filter(function (v) {
      var val = v.o[product.sizeIdx];
      if (norm(val) === nb) return true;
      if (norm(val).split(/\s*\/\s*/).indexOf(nb) > -1) return true;
      return chipsOf(val, product.ctx).some(function (c) { return want.indexOf(c.v) > -1; });
    });
    if (!hits.length) return [];
    if (product.lenIdx > -1) {
      var bucket = x ? lengthBucket(x.length) || null : null;
      var lenText = x ? norm(x.length).replace(/"/g, "") : "";
      var byLen = hits.filter(function (v) {
        var lv = norm(v.o[product.lenIdx]);
        if (!x) return lengthBucket(lv) === "Regular" || /standard|regular/.test(lv);
        return lv === lenText || lv.indexOf(lenText + " ") === 0 || lv.indexOf(lenText + "(") === 0 || (bucket && lengthBucket(lv) === bucket);
      });
      if (byLen.length) hits = byLen;
    }
    return hits;
  }

  root.PmoFit = {
    AREAS: AREAS.map(function (a) { return a[0]; }),
    areaKey: areaKey,
    chipsOf: chipsOf,
    chipRank: chipRank,
    lengthBucket: lengthBucket,
    splitLength: splitLength,
    loadProfile: loadProfile,
    saveProfile: saveProfile,
    measValue: measValue,
    fitProduct: fitProduct,
    variantsFor: variantsFor,
    LEVEL_RANK: LEVEL_RANK
  };
})(typeof self !== "undefined" ? self : this);
